import { NotificationChannel } from "../../types/alerts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Plus } from "lucide-react";
import { CreateChannelDialog } from "./CreateChannelDialog";

interface ChannelSelectionProps {
  channels: NotificationChannel[];
  selectedChannels: string[];
  onChannelsChange: (channels: NotificationChannel[]) => void;
  onSelectionChange: (selectedChannels: string[]) => void;
}

export function ChannelSelection({
  channels,
  selectedChannels,
  onChannelsChange,
  onSelectionChange,
}: ChannelSelectionProps) {
  const enabledChannels = channels.filter((ch) => ch.enabled);

  const toggleChannel = (channelId: string) => {
    onSelectionChange(
      selectedChannels.includes(channelId)
        ? selectedChannels.filter((id) => id !== channelId)
        : [...selectedChannels, channelId]
    );
  };

  const handleChannelCreated = (newChannel: NotificationChannel) => {
    onChannelsChange([...channels, newChannel]);
    onSelectionChange([...selectedChannels, newChannel.id]);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Send to Channel(s)</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          You can enable multiple notification channels to send alerts to multiple destinations.
        </p>

        <div className="space-y-3">
          {enabledChannels.length === 0 ? (
            <div className="text-center py-8 border rounded-md">
              <p className="text-sm text-muted-foreground mb-2">No channels found.</p>
              <p className="text-sm text-muted-foreground mb-4">
                You must create and select at least one notification channel.
              </p>
            </div>
          ) : (
            enabledChannels.map((channel) => (
              <div
                key={channel.id}
                className="flex items-center space-x-3 p-3 border rounded-md hover:bg-muted/50"
              >
                <Checkbox
                  id={`channel-${channel.id}`}
                  checked={selectedChannels.includes(channel.id)}
                  onCheckedChange={() => toggleChannel(channel.id)}
                />
                <Label
                  htmlFor={`channel-${channel.id}`}
                  className="flex-1 cursor-pointer flex items-center justify-between"
                >
                  <div>
                    <div className="font-medium">{channel.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {channel.type} •{" "}
                      {channel.config.email ||
                        channel.config.slackChannel ||
                        channel.config.webhookUrl ||
                        channel.config.serviceKey ||
                        (channel.type === "jira"
                          ? `Project: ${channel.config.jiraProjectKey || "N/A"}`
                          : "configured")}
                    </div>
                  </div>
                  <Badge variant="secondary" className="text-xs">
                    {channel.type}
                  </Badge>
                </Label>
              </div>
            ))
          )}
          <CreateChannelDialog onChannelCreated={handleChannelCreated}>
            <Button variant="outline" size="sm" className="w-full">
              <Plus className="h-4 w-4 mr-2" />
              New channel
            </Button>
          </CreateChannelDialog>
        </div>
      </CardContent>
    </Card>
  );
}
